import { Loader2, ClipboardPaste, AlertTriangle } from "lucide-react";
import DOMPurify from "dompurify";
import MarkdownRenderer from "@/components/shared/MarkdownRenderer";
import UrlPreviewCard from "@/components/shared/UrlPreviewCard";
import { useI18n } from "@/lib/i18n";
import type { ClipboardPreview } from "@/types";

interface PreviewContentRendererProps {
  loading: boolean;
  error: string | null;
  preview: ClipboardPreview | null;
  showRawSource: boolean;
  editableText: string;
  setEditableText: (v: string) => void;
  setShowRawSource: (v: boolean) => void;
}

export function PreviewContentRenderer({
  loading,
  error,
  preview,
  showRawSource,
  editableText,
  setEditableText,
  setShowRawSource,
}: PreviewContentRendererProps) {
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="text-center text-muted-foreground">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="text-center text-muted-foreground">
        <ClipboardPaste className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('preview.click_paste')}</p>
      </div>
    );
  }

  if (preview.type === 'empty') {
    return (
      <div className="text-center text-muted-foreground">
        <ClipboardPaste className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">{t('preview.empty')}</p>
      </div>
    );
  }

  if (preview.type === 'unsupported') {
    return (
      <div className="text-center text-destructive/70">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('preview.unsupported')}</p>
      </div>
    );
  }

  if (preview.type === 'image') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <img src={preview.content} alt="clipboard" className="max-w-full max-h-full object-contain rounded-lg" />
      </div>
    );
  }

  if (preview.type === 'html') {
    if (showRawSource) {
      return (
        <textarea
          autoFocus
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowRawSource(false);
          }}
          className="w-full h-full resize-none rounded-lg border border-border/50 bg-background/30 px-3 pt-4 pb-3 text-xs text-muted-foreground font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring/60 overflow-y-auto"
          style={{ lineHeight: "1.6", boxSizing: "border-box" }}
        />
      );
    }
    
    const cleanHtml = DOMPurify.sanitize(editableText);

    return (
      <div
        className="w-full min-h-full text-sm text-foreground prose prose-sm max-w-none select-text cursor-text pt-2"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    );
  }

  if (preview.type === 'markdown') {
    if (showRawSource) {
      return (
        <textarea
          autoFocus
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowRawSource(false);
          }}
          className="w-full h-full resize-none rounded-lg border border-border/50 bg-background/30 px-3 pt-4 pb-3 text-xs text-muted-foreground font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring/60 overflow-y-auto"
          style={{ lineHeight: "1.6", boxSizing: "border-box" }}
        />
      );
    }

    return (
      <div className="w-full h-full select-text cursor-text pt-1">
        <MarkdownRenderer content={editableText} />
      </div>
    );
  }

  if (preview.type === 'json') {
    if (showRawSource) {
      return (
        <textarea
          autoFocus
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowRawSource(false);
          }}
          className="w-full h-full resize-none rounded-lg border border-border/50 bg-background/30 px-3 pt-4 pb-3 text-xs text-muted-foreground font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring/60 overflow-y-auto"
          style={{ lineHeight: "1.6", boxSizing: "border-box" }}
        />
      );
    }

    return (
      <pre className="w-full h-full select-text cursor-text pt-2 text-xs text-foreground/80 font-mono whitespace-pre overflow-auto">
        {editableText}
      </pre>
    );
  }

  if (preview.type === 'url') {
    if (showRawSource) {
      return (
        <textarea
          autoFocus
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowRawSource(false);
          }}
          className="w-full h-full resize-none rounded-lg border border-border/50 bg-background/30 px-3 pt-4 pb-3 text-xs text-muted-foreground font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring/60 overflow-y-auto"
          style={{ lineHeight: "1.6", boxSizing: "border-box" }}
        />
      );
    }

    return (
      <div className="w-full h-full select-none cursor-default py-2">
        <UrlPreviewCard url={editableText} />
      </div>
    );
  }

  if (preview.type === 'text') {
    if (showRawSource) {
      return (
        <textarea
          autoFocus
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowRawSource(false);
          }}
          className="w-full h-full resize-none rounded-lg border border-border/50 bg-background/30 px-3 pt-4 pb-3 text-sm text-foreground leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring/60 overflow-y-auto"
          style={{ lineHeight: "1.75", boxSizing: "border-box" }}
        />
      );
    }

    return (
      <div className="w-full min-h-full text-sm text-foreground select-text cursor-text pt-2 whitespace-pre-wrap leading-relaxed">
        {editableText}
      </div>
    );
  }

  return null;
}
